import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getTenantUsersOptions } from '@/features/tenants/api/tenant-users';

import { useCreateUserToken } from '../../api/user-token';
import type { useModal } from '@/components/modals/new/use-modal';
import { Modal } from '@/components/modals/new/modal';

export interface AddContactModalProps {
  modal: ReturnType<typeof useModal>;
  tenantId: string;
}

export const AddContactModal: React.FC<AddContactModalProps> = ({ tenantId, modal }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useCreateUserToken({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('successfully-invited-contact'));
        queryClient.invalidateQueries({ queryKey: getTenantUsersOptions(tenantId).queryKey });
        // TODO: send the token
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const schema = z.object({
    email: z.string().email(),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    mutate({
      ...data,
      tenantId,
      type: 'contact',
      role: 'client',
    });
    modal.dismiss();
  };

  return (
    <Modal {...modal} className="min-w-[360px] max-w-min" title={t('add-contact')}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Modal.Body>
            <Controller
              name="email"
              control={form.control}
              render={({ field, formState }) => (
                <Input
                  {...field}
                  label={t('email')}
                  type="email"
                  errors={[formState.errors.email?.message || '']}
                />
              )}
            />
          </Modal.Body>
          <Modal.Actions className="sm:justify-end">
            <div className="flex flex-row gap-2">
              <Button variant="contained" type="submit">
                {t('submit')}
              </Button>
              <Button variant="outline" onClick={modal.dismiss}>
                {t('close')}
              </Button>
            </div>
          </Modal.Actions>
        </form>
      </FormProvider>
    </Modal>
  );
};
